import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    animationFrameScheduler,
    merge,
    of,
    scheduled,
    Subject,
    Subscription,
} from 'rxjs';
import { delay, sampleTime, takeUntil } from 'rxjs/operators';

import { takeUntilDestroy } from '../../../../common/take-until-destroy.decorator';

@Component({
    selector: 'reusable-grid-text',
    template: ` <span
            *ngIf="_tooltipText"
            [cmpsTooltipTrigger]="_tooltipText"
        ></span>
        <p #textWrapper>
            {{ _displayText }}
        </p>`,
    host: {
        class: 'reusable-grid-text',
    },
})
export class ReusableGridTextComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    @Input() text: string;
    @Input() resizeSubject: Subject<void>;
    @Input() lineHeight: number;
    @Input() fontSize: number;

    @ViewChild('textWrapper') private readonly _textWrapper: ElementRef;

    /** @private */ _tooltipText: string;
    /** @private */ _displayText: string;
    private _resizeSubscription: Subscription;
    private readonly _elDestroyedSubject: Subject<void>;

    constructor(
        private readonly _el: ElementRef,
        private readonly _changeDetectorRef: ChangeDetectorRef,
    ) {
        this.text = '';
        this._displayText = '';
        this._tooltipText = '';
        this.lineHeight = 1.5;
        this.fontSize = 12;
        this.resizeSubject = new Subject();
        this._elDestroyedSubject = new Subject();
    }

    ngOnInit(): void {
        this._displayText = this.text;
    }

    ngAfterViewInit(): void {
        scheduled(of(null), animationFrameScheduler)
            .pipe(delay(1))
            .subscribe(() => {
                this.setHeight();

                this._onResize();
                this._resizeSubscription = this.resizeSubject
                    .pipe(sampleTime(1500), takeUntilDestroy(this))
                    .subscribe(() => this._onResize());
            });
    }

    ngOnDestroy(): void {
        this._elDestroyedSubject.next();
        this._resizeSubscription?.unsubscribe();
    }

    private setHeight(): void {
        const componentEl: HTMLElement = this._el.nativeElement;
        componentEl.style.lineHeight = this.lineHeight.toString();
        componentEl.style.fontSize = this.fontSize.toString() + 'px';
        componentEl.style.height = `${this.lineHeight * this.fontSize * 2}px`;
    }

    private _onResize(): void {
        if (this.text == null) {
            return;
        }

        const componentEl: HTMLElement = this._el.nativeElement;
        const textWrapper: HTMLElement = this._textWrapper.nativeElement;

        if (textWrapper.offsetHeight <= componentEl.offsetHeight) {
            return;
        }

        const isResizingText = new Subject();
        const isResizingDone = new Subject();
        const forceStop = merge(this._elDestroyedSubject, this.resizeSubject);

        textWrapper.innerText = this.text;

        isResizingDone.pipe(takeUntil(forceStop)).subscribe(() => {
            if (
                textWrapper.innerText.endsWith('...') ||
                textWrapper.scrollWidth > textWrapper.offsetWidth
            ) {
                this._tooltipText = this.text;
            } else {
                this._tooltipText = null;
            }

            this._changeDetectorRef.detectChanges();
        });

        isResizingText.pipe(takeUntil(forceStop)).subscribe(() => {
            const toDisplay = textWrapper.innerText.split(' ');

            if (toDisplay.length <= 1) {
                textWrapper.style.whiteSpace = 'nowrap';
                return isResizingDone.next();
            }

            if (textWrapper.offsetHeight > componentEl.offsetHeight) {
                toDisplay.pop();
                textWrapper.innerText = toDisplay.join(' ') + '...';

                isResizingText.next();
            } else {
                isResizingDone.next();
            }
        });

        isResizingText.next();
    }
}
